import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Upload, Search } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate, MEMBERSHIP_CLS, LICENSE_STATUS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, NativeSelect, Badge, Loading, Empty, AthletePhoto } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AthleteForm } from "@/components/AthleteForm";
import { ImportDialog } from "@/components/ImportDialog";

export default function Athletes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(false);
  const [imp, setImp] = useState(false);

  const load = () => api.get("/athletes", { params: { q, sector_id: sector, membership_status: status } })
    .then((r) => setRows(r.data)).catch((e) => { setRows([]); console.error(errMsg(e)); });
  useEffect(() => { api.get("/sectors").then((r) => setSectors(r.data)); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, sector, status]);

  return (
    <div>
      <PageHeader eyebrow="Anagrafica" title="Atleti" subtitle={user.role === "admin" ? "Tutti gli atleti dei settori motoristici MSP Italia." : `Atleti del ${user.sector_name}.`}>
        <Button variant="outline" onClick={() => setImp(true)} data-testid="athletes-import-btn"><Upload className="mr-2 h-4 w-4" />Importa CSV/Excel</Button>
        <Button onClick={() => setForm(true)} className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="athletes-new-btn"><Plus className="mr-2 h-4 w-4" />Nuovo atleta</Button>
      </PageHeader>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, codice fiscale, tessera, ASD…" className="h-10 bg-white pl-9" data-testid="athletes-search-input" />
        </div>
        {user.role === "admin" && (
          <NativeSelect value={sector} onChange={setSector} testid="athletes-sector-filter">
            <option value="">Tutti i settori</option>
            {sectors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </NativeSelect>
        )}
        <NativeSelect value={status} onChange={setStatus} testid="athletes-status-filter">
          <option value="">Ogni tesseramento</option>
          {Object.keys(MEMBERSHIP_CLS).map((s) => <option key={s}>{s}</option>)}
        </NativeSelect>
      </div>
      {rows === null ? <Loading /> : rows.length === 0 ? <Empty>Nessun atleta trovato.</Empty> : (
        <div className="card-surface overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm" data-testid="athletes-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Atleta</th><th className="px-4 py-3">Codice fiscale</th><th className="px-4 py-3">Tessera MSP</th><th className="px-4 py-3">ASD/SSD</th><th className="px-4 py-3">Settore</th><th className="px-4 py-3">Tesseramento</th><th className="px-4 py-3">Licenze</th></tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} onClick={() => navigate(`/atleti/${a.id}`)} className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-blue-50/40" data-testid={`athlete-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <AthletePhoto athleteId={a.id} hasPhoto={a.has_photo} name={`${a.first_name} ${a.last_name}`} className="h-9 w-9" rounded="rounded-full" />
                      <div><div className="font-semibold text-slate-900">{a.last_name} {a.first_name}</div><div className="text-xs text-slate-500">Nato/a il {fmtDate(a.birth_date)}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{a.tax_code}</td>
                  <td className="px-4 py-3 font-mono text-xs">{a.membership_number}</td>
                  <td className="px-4 py-3 text-slate-600">{a.club || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{sectors.find((s) => s.id === a.sector_id)?.name || "—"}</td>
                  <td className="px-4 py-3"><Badge cls={MEMBERSHIP_CLS[a.membership_status]}>{a.membership_status}</Badge><div className="mt-1 text-xs text-slate-500">fino al {fmtDate(a.membership_expiry)}</div></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {a.licenses.length === 0 ? <span className="text-slate-400">—</span> : a.licenses.map((l, i) => <Badge key={i} cls={LICENSE_STATUS[l.status].cls}>{l.number || "Richiesta"}</Badge>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AthleteForm open={form} onOpenChange={setForm} onSaved={(a) => navigate(`/atleti/${a.id}`)} />
      <ImportDialog open={imp} onOpenChange={setImp} onDone={load} />
    </div>
  );
}
